import z from "zod";

export enum PolicyType {
    Guaranteed = "guaranteed",
    Indicative = "indicative",
    IndicativeAMS = "indicativeAMS",
    Error = "error",
}

const quoteApiPolicy = z.object({
    totalAnnualPension: z.number().optional(),
    position: z.number().nullish(),
    statusCode: z.number().optional(),
    error: z.coerce.boolean().optional(),
    notes: z.array(z.string()).optional(),
    reference: z.string().nullish(),
    isExpired: z.coerce.boolean().nullish(),
    expiryDate: z.coerce.date().nullish(),
    indicative: z.coerce.boolean().optional(),
});

const basePolicy = z.object({
    type: z.nativeEnum(PolicyType),
    totalAnnualPension: z.number(),
    position: z.number().nullish(),
    error: z.boolean(),
});

const erroredPolicy = basePolicy.extend({
    type: z.literal(PolicyType.Error),
    error: z.literal(true).default(true),
    statusCode: z.number(),
});

const nonErroredPolicy = basePolicy.extend({
    expiryDate: z.coerce.date().nullish(),
    isExpired: z.boolean().optional(),
    notes: z.array(z.string()).optional(),
    reference: z.string().nullish(),
    error: z.boolean().default(false),
});

const handoffPolicy = nonErroredPolicy.extend({
    type: z.literal(PolicyType.IndicativeAMS),
});
const guaranteedPolicy = nonErroredPolicy.extend({
    type: z.literal(PolicyType.Guaranteed),
});
const indicativePolicy = nonErroredPolicy.extend({
    type: z.literal(PolicyType.Indicative),
});

const policy = z.discriminatedUnion("type", [
    erroredPolicy,
    handoffPolicy,
    guaranteedPolicy,
    indicativePolicy,
]);

const basePremium = z.object({
    premiumId: z.number(),
    company: z.object({
        slug: z.string(),
        name: z.string(),
        privacyUrl: z.string().optional(),
        phone: z.string().optional(),
    }),
    documents: z.object({
        illustration: z.string().nullable(),
        termsConditions: z.string().nullable(),
        keyFeatures: z.string().nullable(),
    }).optional(),
    datapoints: z.object({
        description: z.string().optional(),
    }).optional(),
    quoteGeneratedBy: z.enum(["Extranet", "AMS", "Annuity Ready"]).nullable().optional(),
});

const quoteApiPremium = basePremium.extend({
    policy: quoteApiPolicy,
});

const premium = basePremium.extend({
    policy,
});

type QuoteApiPremium = z.infer<typeof quoteApiPremium>;
type QuoteApiPolicy = z.infer<typeof quoteApiPolicy>;

const transformQuoteApiPolicy = (quoteGeneratedBy: QuoteApiPremium["quoteGeneratedBy"]) => (data: QuoteApiPolicy) => {
    const {
        totalAnnualPension,
        position,
        statusCode,
        error,
        notes,
        reference,
        isExpired,
        expiryDate,
        indicative,
    } = data;

    if (error) {
        return {
            type: PolicyType.Error,
            totalAnnualPension,
            position,
            statusCode,
            error,
        };
    } else if (indicative === true) {
        if (quoteGeneratedBy === "AMS") {
            return {
                type: PolicyType.IndicativeAMS,
                totalAnnualPension,
                position,
                notes,
                reference,
                isExpired,
                expiryDate,
            };
        } else {
            return {
                type: PolicyType.Indicative,
                totalAnnualPension,
                position,
                notes,
                reference,
                isExpired,
                expiryDate,
            };
        }
    } else {
        return {
            type: PolicyType.Guaranteed,
            totalAnnualPension,
            position,
            notes,
            reference,
            isExpired,
            expiryDate,
        };
    }
};

const transformQuoteApiPremium = (data: QuoteApiPremium) => {
    return {
        ...data,
        policy: transformQuoteApiPolicy(data.quoteGeneratedBy)(data.policy),
    };
};

export const validateAnnuitiesPremium = quoteApiPremium.transform(transformQuoteApiPremium).pipe(premium);

export type AnnuitiesPremium = z.infer<typeof validateAnnuitiesPremium>;
export type Policy = z.infer<typeof policy>;
export type NarrowedPremium<T extends PolicyType> = AnnuitiesPremium & { policy: Extract<Policy, { type: T }> };
export type Documents = Exclude<AnnuitiesPremium["documents"], undefined>;

export const policyIsType = <T extends PolicyType>(
    premium: AnnuitiesPremium,
    type: T,
): premium is NarrowedPremium<T> => {
    return premium.policy.type === type;
};

export const policyIsNotType = <T extends PolicyType>(
    premium: AnnuitiesPremium,
    type: T,
): premium is NarrowedPremium<Exclude<PolicyType, T>> => {
    return premium.policy.type !== type;
};
